/**
 * Онбординг: от «кто вы» до рабочего состояния.
 *
 * Цель из плана — 90 секунд от /start. Поэтому у родителя ровно один экран
 * с тремя полями, а у няни экрана настройки нет вообще: она приходит по
 * ссылке, где всё уже задано.
 */

import { useEffect, useState } from 'react'

import { track } from '@/shared/analytics'
import { useCreateFamily } from '@/shared/api/mutations'
import type { Role } from '@/shared/api/types'
import { parseInviteToken } from '@/shared/invite'
import { haptic, shareInviteCard } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { Screen } from '@/shared/ui/Screen'
import { Field, MoneyInput, Segmented, TextInput } from '@/shared/ui/fields'
import { Card } from '@/shared/ui/primitives'
import { ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

type Step = 'role' | 'parent-setup' | 'invite' | 'nanny-wait'

export function Onboarding({
  name,
  onInvite,
}: {
  name: string
  /** Няня вставила ссылку-приглашение руками. */
  onInvite: (token: string) => void
}) {
  const [step, setStep] = useState<Step>('role')
  const [inviteLink, setInviteLink] = useState<string>('')

  // Начало воронки. Её конец (onboarding_completed) пишет сервер в момент
  // создания семьи — так шаги нельзя рассинхронизировать.
  useEffect(() => track('onboarding_started'), [])

  if (step === 'role') {
    return (
      <RolePick
        name={name}
        onPick={(role) => setStep(role === 'parent' ? 'parent-setup' : 'nanny-wait')}
      />
    )
  }

  if (step === 'parent-setup') {
    return (
      <ParentSetup
        onCreated={(link) => {
          setInviteLink(link)
          setStep('invite')
        }}
        onBack={() => setStep('role')}
      />
    )
  }

  if (step === 'invite') return <InviteShare link={inviteLink} />

  return <NannyWaiting onBack={() => setStep('role')} onInvite={onInvite} />
}

/* ─── Шаг 1: кто вы ──────────────────────────────────────────────────────── */

function RolePick({ name, onPick }: { name: string; onPick: (role: Role) => void }) {
  return (
    <Screen withTabBar={false}>
      <div className="flex flex-col items-center px-2 pt-8 pb-8 text-center">
        <div
          aria-hidden
          className="sticker bg-surface pop mb-5 flex size-[84px] rotate-[-5deg] items-center justify-center rounded-[var(--radius-sheet)]"
        >
          <Icon name="brand-hello" size={36} />
        </div>
        <h1 className="font-display text-screen leading-tight">Здравствуйте, {name}!</h1>
        <p className="font-hand text-text-muted mt-1.5 text-lead leading-tight">
          давайте настроим за минуту
        </p>
        <p className="text-text-muted mt-3 max-w-[300px] text-body leading-snug">
          ЯНяня — общий журнал дня ребёнка. День, часы, расходы и расчёт в одном
          месте, и обе стороны видят одно и то же.
        </p>
      </div>

      <RoleCard
        icon="role-parent"
        title="Я родитель"
        description="Создам семью, приглашу няню и буду видеть день ребёнка"
        onClick={() => onPick('parent')}
      />
      <RoleCard
        icon="role-nanny"
        title="Я няня"
        description="Меня пригласили — войду по ссылке от родителей"
        onClick={() => onPick('nanny')}
      />
    </Screen>
  )
}

function RoleCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: IconName
  title: string
  description: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('tap')
        onClick()
      }}
      className="press sticker bg-surface flex w-full items-center gap-4 rounded-[var(--radius-card)] p-4 text-left active:shadow-[var(--shadow-raised)]"
    >
      <span
        aria-hidden
        className="bg-accent-soft border-line/60 flex size-12 shrink-0 items-center justify-center rounded-[var(--radius-button)] border"
      >
        <Icon name={icon} size={25} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-display block text-[18px] leading-tight">{title}</span>
        <span className="text-text-muted block text-[13px] leading-snug">{description}</span>
      </span>
      <Icon name="action-chevron" size={21} />
    </button>
  )
}

/* ─── Шаг 2: родитель настраивает семью ──────────────────────────────────── */

const PERIODS = [
  { value: 'week' as const, label: 'Неделя' },
  { value: 'halfmonth' as const, label: '2 раза в месяц' },
  { value: 'month' as const, label: 'Месяц' },
]

function ParentSetup({
  onCreated,
  onBack,
}: {
  onCreated: (link: string) => void
  onBack: () => void
}) {
  const [childName, setChildName] = useState('')
  const [rate, setRate] = useState('')
  const [period, setPeriod] = useState<'week' | 'halfmonth' | 'month'>('halfmonth')

  const createFamily = useCreateFamily({
    onSuccess: (result) => {
      haptic('success')
      onCreated(result.invite.link)
    },
  })

  const canSubmit = childName.trim().length > 0 && !createFamily.isPending

  return (
    <Screen
      title="Немного о вашей семье"
      subtitle="Это можно будет поменять в любой момент"
      withTabBar={false}
    >
      {createFamily.error && (
        <ErrorBanner
          message={createFamily.error.message}
          onRetry={() => createFamily.reset()}
        />
      )}

      <Card className="space-y-4">
        <Field label="Как зовут ребёнка">
          <TextInput
            value={childName}
            onChange={(event) => setChildName(event.target.value)}
            placeholder="Ваня"
            autoFocus
            maxLength={64}
          />
        </Field>

        <Field
          label="Ставка няни"
          hint="Часы посчитаются автоматически из смен. Ставку увидит только няня и вы."
        >
          <MoneyInput value={rate} onValueChange={setRate} suffix="/ час" />
        </Field>

        <Field label="Как часто рассчитываетесь">
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
        </Field>
      </Card>

      <Button
        block
        size="lg"
        loading={createFamily.isPending}
        disabled={!canSubmit}
        onClick={() =>
          createFamily.mutate({
            child_name: childName.trim(),
            nanny_rate: rate || undefined,
            timesheet_period: period,
          })
        }
      >
        Продолжить
      </Button>
      <Button block variant="ghost" onClick={onBack} hapticKind="none">
        Назад
      </Button>
    </Screen>
  )
}

/* ─── Шаг 3: пригласить няню ─────────────────────────────────────────────── */

function InviteShare({ link }: { link: string }) {
  return (
    <Screen withTabBar={false}>
      <div className="flex flex-col items-center px-2 pt-10 pb-6 text-center">
        <div
          aria-hidden
          className="bg-accent-soft mb-5 flex size-20 items-center justify-center rounded-[var(--radius-sheet)]"
        >
          <Icon name="invite-link" size={36} />
        </div>
        <h1 className="font-display text-title leading-tight font-extrabold">
          Семья готова
        </h1>
        <p className="text-text-muted mt-2 max-w-[300px] text-body leading-snug">
          Отправьте няне ссылку в Telegram. Она откроет приложение и сразу увидит свой день.
          Ссылка действует три дня.
        </p>
      </div>

      <Button
        block
        size="lg"
        onClick={() =>
          shareInviteCard(
            parseInviteToken(link),
            link,
            'Приглашаю вас в ЯНяню — наш общий журнал дня ребёнка',
          )
        }
      >
        Отправить приглашение
      </Button>

      <Button
        block
        variant="secondary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(link)
            toast.success('Ссылка скопирована')
          } catch {
            toast.error('Не удалось скопировать — отправьте через Telegram')
          }
        }}
      >
        Скопировать ссылку
      </Button>

      <p className="text-text-muted px-4 pt-2 text-center text-caption leading-snug">
        Можно отправить позже — ссылка всегда лежит на экране «Семья».
      </p>
    </Screen>
  )
}

/* ─── Няня без приглашения ───────────────────────────────────────────────── */

function NannyWaiting({
  onBack,
  onInvite,
}: {
  onBack: () => void
  onInvite: (token: string) => void
}) {
  const [pasted, setPasted] = useState('')
  const [invalid, setInvalid] = useState(false)

  // Запасной путь: ссылка открыла чат, а не приложение, и приглашение
  // потерялось по дороге. Вставленная руками ссылка ведёт туда же.
  const submit = () => {
    const token = parseInviteToken(pasted)
    if (!token) {
      setInvalid(true)
      haptic('error')
      return
    }
    haptic('success')
    onInvite(token)
  }

  return (
    <Screen withTabBar={false}>
      <div className="flex flex-col items-center px-2 pt-10 pb-6 text-center">
        <div
          aria-hidden
          className="bg-accent-soft mb-5 flex size-20 items-center justify-center rounded-[var(--radius-sheet)]"
        >
          <Icon name="invite-send" size={36} />
        </div>
        <h1 className="font-display text-title leading-tight font-extrabold">
          Нужна ссылка от родителей
        </h1>
        <p className="text-text-muted mt-2 max-w-[310px] text-body leading-snug">
          Попросите родителей открыть ЯНяню и нажать «Пригласить няню». Придёт ссылка —
          откройте её, и вы окажетесь здесь же, но уже со своим днём.
        </p>
      </div>

      <Card>
        <h2 className="text-body font-semibold">Ссылка уже есть?</h2>
        <p className="text-text-muted mt-1 text-note leading-snug">
          Если по ссылке открылся чат, а не приложение, скопируйте её и вставьте сюда.
        </p>
        <div className="mt-3 space-y-2">
          <Field
            error={invalid ? 'Не похоже на приглашение — скопируйте ссылку целиком' : undefined}
          >
            <TextInput
              value={pasted}
              invalid={invalid}
              placeholder="Ссылка от родителей"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              onChange={(event) => {
                setPasted(event.target.value)
                setInvalid(false)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit()
              }}
            />
          </Field>
          <Button block disabled={!pasted.trim()} onClick={submit}>
            Открыть приглашение
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="text-body font-semibold">Что вы получите</h2>
        <ul className="text-text-muted mt-2 space-y-2 text-note leading-snug">
          <li>· День ребёнка отмечается в один тап — без отчётов вечером</li>
          <li>· Часы считаются сами, расходы копятся и приходят в расчёте</li>
          <li>· Геометки — только по вашему нажатию и только с вашего согласия</li>
        </ul>
      </Card>

      <Button block variant="ghost" onClick={onBack} hapticKind="none">
        Я вообще-то родитель
      </Button>
    </Screen>
  )
}
